import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Commencez à taper une adresse...", 
  name,
  required = false,
  className = "",
  onAddressSelect = null // Callback pour récupérer les détails complets de l'adresse
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fonction pour rechercher les adresses via l'API française
  const searchAddresses = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=10`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.features || []);
      } else {
        console.error('Erreur API adresse:', response.status);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresses:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce pour éviter trop de requêtes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (value && value.length >= 3) {
        searchAddresses(value);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [value]);

  // Gérer la sélection d'une suggestion
  const handleSuggestionClick = (suggestion) => {
    const address = suggestion.properties.label;
    onChange({ target: { name, value: address } });
    
    // Appeler le callback avec les détails complets si fourni
    if (onAddressSelect) {
      onAddressSelect({
        label: address,
        coordinates: suggestion.geometry.coordinates, // [longitude, latitude]
        city: suggestion.properties.city,
        postcode: suggestion.properties.postcode,
        context: suggestion.properties.context,
        properties: suggestion.properties
      });
    }
    
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // Gérer les touches du clavier
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Fermer les suggestions si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`address-autocomplete ${className}`}>
      <div className="address-input-container">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          required={required}
          className="form-control"
          autoComplete="off"
        />
        
        {isLoading && (
          <div className="address-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="address-suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.properties.id}-${index}`}
              className={`address-suggestion ${
                index === selectedIndex ? 'selected' : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-main">
                {suggestion.properties.label}
              </div>
              <div className="suggestion-details">
                {suggestion.properties.context}
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && !isLoading && value.length >= 3 && (
        <div className="address-suggestions">
          <div className="address-suggestion no-results">
            Aucune adresse trouvée
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
